/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { TrainingGetStatusSwagger } from 'src/luis/core/services/training/trainingSwagger';
import { ModelTrainingStatus, TrainingState } from 'src/luis/types/trainingTypes';

/**
 * Mapper function to map the objects returned from the API to ModelTrainStatus objects
 *
 * @param modelsSwagger The objects returned from the API
 * @returns An array of model training status objects
 */
export const modelTrainingStatusSwaggerToModelTrainingStatus = (trainingSwagger: TrainingGetStatusSwagger): ModelTrainingStatus[] => {
    const modelsSwagger = Array.isArray(trainingSwagger) ? trainingSwagger : trainingSwagger.trainingStatusDetails;

    return modelsSwagger.map(model => ({
        id: model.modelId,
        statusId: statusIdMapper(model.details.statusId),
        progressSubStatus: progressSubStatusMapper(model.details.progressSubstatus),
        error: model.details.failureReasonMessage ?? model.details.failureReason
    }));
};

/**
 * Maps the status id returned in the swagger
 * to a training state.
 *
 * @param id The status id received.
 * @returns A training state string.
 */
const statusIdMapper = (id: number): TrainingState => {
    switch (id) {
        case 0:
            return 'success';
        case 1:
            return 'fail';
        case 2:
            return 'upToDate';
        case 3:
            return 'inProgress';
        case 7:
            return 'sampling';
        case 9:
            return 'queued';
        default:
            throw Error(`Not a valid status id: ${id}`);
    }
};

/**
 * Returns a sub-status string based on the status id
 * received in the swagger.
 *
 * @param id The sub-status id.
 * @returns The sub-status string.
 */
const progressSubStatusMapper = (id: string): TrainingState => {
    if (!id) {
        return;
    }

    switch (id) {
        case 'CollectingData':
            return 'collectingData';
        case 'UploadingModel':
            return 'uploadingModel';
        case 'InProgress':
            return 'inProgress';
        default:
            return 'none';
    }
};
